import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { AiStudyGenerationApi } from '../../../services/ai/AiStudyGenerationApi';
import { AiConversationsApi } from '../../../services/ai/AiConversationsApi';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import RichMarkdown from '../../../components/common/RichMarkdown';

const TabIcons = {
  flashcards: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 4v16"/><path d="M2 12h20"/>
    </svg>
  ),
  quiz: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  chat: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  audio: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
};

const TABS = [
  { id: 'flashcards', label: 'Flashcards',    icon: TabIcons.flashcards, tag: 'Quick recall',    desc: 'Generate flashcards from your course materials', step: 1 },
  { id: 'quiz',       label: 'Quiz',           icon: TabIcons.quiz,       tag: 'Test yourself',   desc: 'Test your knowledge with AI-generated questions', step: 2 },
  { id: 'chat',       label: 'Ask Scout',      icon: TabIcons.chat,       tag: 'Get answers',     desc: 'Chat with your AI study assistant',               step: 3 },
  { id: 'audio',      label: 'Audio Review',   icon: TabIcons.audio,      tag: 'Listen & learn',  desc: 'Listen to your materials on the go',              step: 4 },
];

const SUGGESTED_PROMPTS = [
  "What's on my schedule this week?",
  "Help me study for my next exam",
  "Summarize my course materials",
  "Create a study plan for me",
];

/**
 * AI Tools Step — Unified hub for trying AI study tools during onboarding.
 * Contains tabs: Flashcards, Quiz, Ask Scout, Audio Review.
 * When multiple courses exist, shows a course selector at the top.
 */
export default function AIToolsStep({ data, updateData, goNext, goBack, skipStep, user: userProp }) {
  const { user: authUser } = useAuth();
  const user = userProp || authUser;
  const [activeTab, setActiveTab] = useState(null);

  // Course selector — which course powers the AI demos
  const courses = useMemo(() => (data.courses || []).filter(Boolean), [data.courses]);
  const selectedCourseId = data.aiToolsCourseId || courses[0]?.courseId || data.courseId;
  const selectedCourse = useMemo(() => courses.find(c => c?.courseId === selectedCourseId) || courses[0] || {}, [courses, selectedCourseId]);

  const handleCourseChange = useCallback((e) => {
    const newCourseId = e.target.value;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'course_selector_change',
      category: 'onboarding',
      label: 'ai-tools',
      metadata: { fromCourseId: selectedCourseId, toCourseId: newCourseId },
    });
    updateData({ aiToolsCourseId: newCourseId });
  }, [updateData, selectedCourseId]);

  // Collect all document IDs available for the selected course
  const docIdsForCourse = useMemo(() => {
    const ids = new Set();
    // Course-specific docs
    if (selectedCourse.uploadedDocIds) selectedCourse.uploadedDocIds.forEach(id => ids.add(id));
    if (selectedCourse.syllabusDocumentId) ids.add(selectedCourse.syllabusDocumentId);
    // Fallback: global docs
    if (ids.size === 0) {
      if (data.syllabusDocumentId) ids.add(data.syllabusDocumentId);
      if (data.uploadedDocIds) data.uploadedDocIds.forEach(id => ids.add(id));
      if (data.syllabusDocumentIds) data.syllabusDocumentIds.forEach(id => ids.add(id));
    }
    return [...ids].filter(Boolean);
  }, [selectedCourse, data.syllabusDocumentId, data.uploadedDocIds, data.syllabusDocumentIds]);

  const hasDocuments = docIdsForCourse.length > 0;

  return (
    <div className="onboarding-card onboarding-ai-tools">
      {/* ── Compact header row ── */}
      <div className="ai-tools-header-row">
        <div className="ai-tools-header-left">
          <span className="ai-tools-header-icon">✨</span>
          <h2 className="ai-tools-header-title">Your AI Study Tools</h2>
        </div>
        {courses.length > 1 ? (
          <div className="ai-tools-course-badge">
            <span className="ai-tools-course-badge-label">Course</span>
            <select
              id="ai-course-select"
              className="ai-tools-course-select"
              value={selectedCourseId || ''}
              onChange={handleCourseChange}
            >
              {courses.map(c => (
                <option key={c.courseId} value={c.courseId}>{c.courseName}</option>
              ))}
            </select>
          </div>
        ) : selectedCourse.courseName ? (
          <div className="ai-tools-course-badge">
            <span className="ai-tools-course-badge-label">Course</span>
            <span className="ai-tools-course-badge-name">{selectedCourse.courseName}</span>
          </div>
        ) : null}
      </div>

      {/* ── Tool card grid (or active tool panel) ── */}
      {!activeTab ? (
        <div className="ai-tools-card-grid">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className="ai-tools-card"
              onClick={() => {
                UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
                  action: 'tab_switch',
                  category: 'onboarding',
                  label: 'ai-tools',
                  metadata: { fromTab: null, toTab: tab.id },
                });
                setActiveTab(tab.id);
              }}
            >
              <span className="ai-tools-card__icon">{tab.icon}</span>
              <span className="ai-tools-card__name">{tab.label}</span>
              <span className="ai-tools-card__desc">{tab.desc}</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* ── Compact tool strip ── */}
          <div className="ai-tool-strip">
            <div className="ai-tool-strip__tools">
              {TABS.map((tab, idx) => {
                const isActive = activeTab === tab.id;
                const activeIdx = TABS.findIndex(t => t.id === activeTab);
                const isPast = idx < activeIdx;
                return (
                  <button
                    key={tab.id}
                    className={`ai-tool-strip__item${isActive ? ' ai-tool-strip__item--active' : ''}${isPast ? ' ai-tool-strip__item--past' : ''}`}
                    onClick={() => {
                      if (tab.id !== activeTab) {
                        UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
                          action: 'tab_switch',
                          category: 'onboarding',
                          label: 'ai-tools',
                          metadata: { fromTab: activeTab, toTab: tab.id },
                        });
                      }
                      setActiveTab(tab.id);
                    }}
                  >
                    <span className="ai-tool-strip__icon">{tab.icon}</span>
                    <span className="ai-tool-strip__label">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="onboarding-ai-tab-content">
            {activeTab === 'flashcards' && (
              <FlashcardsPanel
                data={data}
                updateData={updateData}
                user={user}
                courseId={selectedCourseId}
                courseName={selectedCourse.courseName || data.courseName}
                docIds={docIdsForCourse}
                hasDocuments={hasDocuments}
              />
            )}
            {activeTab === 'quiz' && (
              <QuizPanel
                data={data}
                updateData={updateData}
                user={user}
                courseId={selectedCourseId}
                courseName={selectedCourse.courseName || data.courseName}
                docIds={docIdsForCourse}
                hasDocuments={hasDocuments}
              />
            )}
            {activeTab === 'chat' && (
              <ChatPanel
                data={data}
                user={user}
                courseId={selectedCourseId}
              />
            )}
            {activeTab === 'audio' && (
              <AudioReviewPanel />
            )}
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="onboarding-actions">
        <button className="onboarding-btn secondary" onClick={goBack}>
          ← Back
        </button>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button className="onboarding-btn ghost" onClick={skipStep}>
            Skip for now
          </button>
          <button className="onboarding-btn primary" onClick={goNext}>
            Finish Setup →
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Flashcards Panel ─────────────────────────────────────────────
function FlashcardsPanel({ data, updateData, user, courseId, courseName, docIds, hasDocuments }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(data.flashcards?.length > 0);
  const [studyMode, setStudyMode] = useState(data.flashcards?.length > 0);
  const [studyIndex, setStudyIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showGeneratedMsg, setShowGeneratedMsg] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);

    try {
      const documentIds = docIds.slice(0, 3);
      if (documentIds.length === 0) {
        setError('No documents available to generate flashcards from.');
        return;
      }

      const result = await AiStudyGenerationApi.generateFlashcards({
        document_ids: documentIds.map(Number),
        num_cards: 5,
        endeavor_id: courseId ? Number(courseId) : undefined,
        user_id: Number(user?.userId || user?.id),
        title: `${courseName || 'Course'} — Quick Start Flashcards`,
        description: 'Auto-generated during onboarding',
      });

      const cards = result?.flashcards || result?.cards || [];
      updateData({
        flashcardSetId: result?.deck_id || result?.flashcard_set_id || null,
        flashcards: cards.slice(0, 5),
      });
      setGenerated(true);
      setStudyMode(true);
      setShowGeneratedMsg(true);
      setTimeout(() => setShowGeneratedMsg(false), 10000);

      UsageEventsTracker.recordUserAction('ONBOARDING_FLASHCARDS_GENERATED', {
        action: 'generate',
        category: 'onboarding',
        entityType: 'flashcard_set',
        entityId: result?.deck_id,
        metadata: { count: cards.length, courseId },
      });
    } catch (err) {
      console.error('[FlashcardsPanel] Generation failed:', err);
      setError(err.message || 'Flashcard generation failed. You can try again or skip.');
    } finally {
      setGenerating(false);
    }
  }, [generating, docIds, courseId, courseName, user, updateData]);

  const displayCards = data.flashcards || [];

  return (
    <div className="onboarding-ai-panel">
      <div className="ai-mode-header ai-mode-header--flashcards">
        <span className="ai-mode-header__bar" />
        <p className="ai-mode-header__line">
          <strong>Flashcards</strong> — {hasDocuments ? 'Turn your materials into instant recall' : 'Upload documents first to generate flashcards'}
        </p>
      </div>

      {!generated && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {generating ? (
            <>
              <div className="onboarding-scan-spinner" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '14px', color: 'var(--semantic-text-secondary)' }}>
                Generating flashcards...
              </p>
            </>
          ) : (
            <button
              className="onboarding-btn primary"
              onClick={handleGenerate}
              disabled={!hasDocuments}
            >
              Generate 5 Flashcards
            </button>
          )}
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--color-error, #ef4444)', fontSize: '14px', textAlign: 'center', margin: '12px 0' }}>
          {typeof error === 'string' ? error : String(error)}
        </p>
      )}

      {generated && displayCards.length > 0 && (
        <>
          {showGeneratedMsg && (
            <p className="ob-fc-generated-msg">
              ✓ {displayCards.length} flashcards generated!
            </p>
          )}

          {studyMode ? (
            <div className="ob-fc-viewer">
              {/* Progress */}
              <div className="ob-fc-progress-row">
                <span className="ob-fc-progress-text">Card {studyIndex + 1} of {displayCards.length}</span>
              </div>
              <div className="ob-fc-progress-bar">
                <div className="ob-fc-progress-fill" style={{ width: `${((studyIndex + 1) / displayCards.length) * 100}%` }} />
              </div>

              {/* Card row with side arrows */}
              <div className="ob-fc-card-row">
                <button
                  className="ob-fc-side-arrow"
                  onClick={() => { setStudyIndex(i => i - 1); setIsFlipped(false); }}
                  disabled={studyIndex === 0}
                  aria-label="Previous card"
                >
                  ‹
                </button>

                <div className="ob-fc-card-container">
                  {/* Deck stack layers */}
                  <div className="ob-fc-deck-stack">
                    <div className="ob-fc-deck-layer ob-fc-deck-layer--2" />
                    <div className="ob-fc-deck-layer ob-fc-deck-layer--1" />
                    <div
                      className="ob-fc-card"
                      onClick={() => {
                        setIsFlipped(f => !f);
                        UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
                          action: 'tool_engage',
                          category: 'onboarding',
                          label: 'ai-tools',
                          metadata: { tool: 'flashcards', interaction: 'flip_card', cardIndex: studyIndex },
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setIsFlipped(f => !f); }
                        if (e.key === 'ArrowRight' && studyIndex < displayCards.length - 1) { setStudyIndex(i => i + 1); setIsFlipped(false); }
                        if (e.key === 'ArrowLeft' && studyIndex > 0) { setStudyIndex(i => i - 1); setIsFlipped(false); }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={isFlipped ? 'Showing answer — click to flip back' : 'Showing question — click to reveal answer'}
                    >
                      <div className={`ob-fc-card-inner${isFlipped ? ' ob-fc-card-inner--flipped' : ''}`}>
                        <div className="ob-fc-card-face ob-fc-card-face--front">
                          <span className="ob-fc-card-label">FRONT</span>
                          <div className="ob-fc-card-text">
                            <RichMarkdown text={displayCards[studyIndex]?.front_text || displayCards[studyIndex]?.front || displayCards[studyIndex]?.question || `Card ${studyIndex + 1}`} />
                          </div>
                          <span className="ob-fc-card-hint">Click or press Space to flip</span>
                        </div>
                        <div className="ob-fc-card-face ob-fc-card-face--back">
                          <span className="ob-fc-card-label">BACK</span>
                          <div className="ob-fc-card-text">
                            <RichMarkdown text={displayCards[studyIndex]?.back_text || displayCards[studyIndex]?.back || displayCards[studyIndex]?.answer || '—'} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  className="ob-fc-side-arrow"
                  onClick={() => { setStudyIndex(i => i + 1); setIsFlipped(false); }}
                  disabled={studyIndex === displayCards.length - 1}
                  aria-label="Next card"
                >
                  ›
                </button>
              </div>

              {/* Utility row */}
              <div className="ob-fc-utility-row">
                <button className="ob-fc-icon-btn" onClick={() => setIsFlipped(f => !f)} title="Flip card">↻</button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}


// ─── Question normalizer (mirrors QuizzesTool.normalizeQuestion) ──
function normalizeQuestion(q) {
  const rawType = q?.question_type || q?.type || q?.questionType || '';
  const question_type = String(rawType).toLowerCase().replace(/\s+/g, '_');
  const question_text = q?.question_text || q?.question || q?.prompt || q?.text || q?.questionText || '';

  // Extract choices from various API response shapes
  const rawChoices =
    Array.isArray(q?.options) ? q.options :
    Array.isArray(q?.choices) ? q.choices :
    Array.isArray(q?.options?.choices) ? q.options.choices :
    Array.isArray(q?.answer_choices) ? q.answer_choices :
    Array.isArray(q?.answers) ? q.answers :
    [];
  const choices = rawChoices.map(o => (typeof o === 'string' ? o : (o?.text ?? o?.label ?? String(o))));

  // Normalize choices for true/false questions
  const normalizedChoices = question_type === 'true_false' ? ['True', 'False'] : choices;

  const rawIdx = q?.correct_index ?? q?.options?.correct_index ?? null;
  const correct_index = typeof rawIdx === 'number' ? rawIdx :
    (typeof rawIdx === 'string' && /^\d+$/.test(rawIdx)) ? parseInt(rawIdx, 10) : null;
  const correct_answer = q?.correct_answer ?? q?.answer ?? q?.correct ?? q?.correctAnswer ?? null;
  const explanation = q?.explanation ?? q?.rationale ?? '';

  return { question_type, question_text, choices: normalizedChoices, correct_index, correct_answer, explanation };
}

// ─── Quiz Panel ───────────────────────────────────────────────────
function QuizPanel({ data, updateData, user, courseId, courseName, docIds, hasDocuments }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(data.quizQuestions?.length > 0);
  const [answers, setAnswers] = useState({});   // questionIndex → selected choice text
  const [submitted, setSubmitted] = useState(false);
  const scoreRef = useRef(null);

  // Normalize stored questions once
  const displayQuestions = useMemo(
    () => (data.quizQuestions || []).map(normalizeQuestion),
    [data.quizQuestions]
  );

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);

    try {
      const documentIds = docIds.slice(0, 3);
      if (documentIds.length === 0) {
        setError('No documents available to generate a quiz from.');
        return;
      }

      const result = await AiStudyGenerationApi.generateQuiz({
        document_ids: documentIds.map(Number),
        num_questions: 5,
        difficulty_level: 'beginner',
        question_type_distribution: { multiple_choice: 3, true_false: 2 },
        endeavor_id: courseId ? Number(courseId) : undefined,
        user_id: Number(user?.userId || user?.id),
        title: `${courseName || 'Course'} — Quick Start Quiz`,
        description: 'Auto-generated during onboarding',
      });

      const questions = result?.questions || [];
      updateData({
        quizSetId: result?.quiz_set_id || result?.quiz_id || null,
        quizQuestions: questions.slice(0, 5),
      });
      setGenerated(true);
      setAnswers({});
      setSubmitted(false);

      UsageEventsTracker.recordUserAction('ONBOARDING_QUIZ_GENERATED', {
        action: 'generate',
        category: 'onboarding',
        entityType: 'quiz_set',
        entityId: result?.quiz_set_id,
        metadata: { count: questions.length, courseId },
      });
    } catch (err) {
      console.error('[QuizPanel] Generation failed:', err);
      setError(err.message || 'Quiz generation failed. You can try again or skip.');
    } finally {
      setGenerating(false);
    }
  }, [generating, docIds, courseId, courseName, user, updateData]);

  const handleSelect = (qIdx, choiceText) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qIdx]: choiceText }));
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'tool_engage',
      category: 'onboarding',
      label: 'ai-tools',
      metadata: { tool: 'quiz', interaction: 'answer_question', questionIndex: qIdx },
    });
  };

  // Determine if a choice is the correct answer for a given question
  const isCorrectChoice = useCallback((q, choiceText, cIdx) => {
    // Prefer correct_index when available
    if (q.correct_index != null && q.correct_index === cIdx) return true;
    // Fall back to matching correct_answer text
    if (q.correct_answer != null) {
      const ca = String(q.correct_answer).trim().toLowerCase();
      const ct = String(choiceText).trim().toLowerCase();
      if (ca === ct) return true;
      // Also match by letter prefix like "A" matching index 0
      if (ca.length === 1 && ca.charCodeAt(0) - 97 === cIdx) return true;
    }
    return false;
  }, []);

  // Find the correct choice text for a question (for showing the answer when user is wrong)
  const getCorrectChoiceText = useCallback((q) => {
    if (q.correct_index != null && q.choices[q.correct_index]) {
      return q.choices[q.correct_index];
    }
    if (q.correct_answer != null) {
      const ca = String(q.correct_answer).trim().toLowerCase();
      const match = q.choices.find(c => String(c).trim().toLowerCase() === ca);
      if (match) return match;
      return String(q.correct_answer);
    }
    return null;
  }, []);

  // Build per-question verdicts for display
  const verdicts = useMemo(() => {
    if (!submitted) return {};
    const v = {};
    displayQuestions.forEach((q, idx) => {
      const userAnswer = answers[idx];
      if (userAnswer == null) {
        v[idx] = 'unanswered';
        return;
      }
      const choiceIdx = q.choices.indexOf(userAnswer);
      v[idx] = isCorrectChoice(q, userAnswer, choiceIdx) ? 'correct' : 'incorrect';
    });
    return v;
  }, [submitted, displayQuestions, answers, isCorrectChoice]);

  const score = useMemo(() => {
    if (!submitted) return null;
    let correct = 0;
    displayQuestions.forEach((_, idx) => {
      if (verdicts[idx] === 'correct') correct++;
    });
    return { correct, total: displayQuestions.length };
  }, [submitted, displayQuestions, verdicts]);

  const handleSubmit = () => {
    setSubmitted(true);
    UsageEventsTracker.recordUserAction('ONBOARDING_QUIZ_SUBMITTED', {
      action: 'submit',
      category: 'onboarding',
      metadata: {
        courseId,
        totalQuestions: displayQuestions.length,
        answeredCount: Object.keys(answers).length,
      },
    });
    // Scroll score into view after React re-renders
    setTimeout(() => {
      scoreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <div className="onboarding-ai-panel">
      <div className="ai-mode-header ai-mode-header--quiz">
        <span className="ai-mode-header__bar" />
        <p className="ai-mode-header__line">
          <strong>Quiz</strong> — {hasDocuments ? 'Test yourself under pressure' : 'Upload documents first to generate a quiz'}
        </p>
      </div>

      {!generated && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {generating ? (
            <>
              <div className="onboarding-scan-spinner" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '14px', color: 'var(--semantic-text-secondary)' }}>
                Generating quiz questions...
              </p>
            </>
          ) : (
            <button
              className="onboarding-btn primary"
              onClick={handleGenerate}
              disabled={!hasDocuments}
            >
              Generate 5-Question Quiz
            </button>
          )}
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--color-error, #ef4444)', fontSize: '14px', textAlign: 'center', margin: '12px 0' }}>
          {typeof error === 'string' ? error : String(error)}
        </p>
      )}

      {generated && displayQuestions.length > 0 && (
        <div className="ob-quiz-questions">
          {displayQuestions.map((q, qIdx) => {
            const userAnswer = answers[qIdx];
            const typeLabel = q.question_type === 'true_false' ? 'True / False' : 'Multiple Choice';
            const verdict = verdicts[qIdx];
            const isAnsweredWrong = verdict === 'incorrect';
            const correctText = isAnsweredWrong ? getCorrectChoiceText(q) : null;
            const isAnswered = !submitted && userAnswer != null;

            return (
              <div key={qIdx} className={`ob-quiz-card${isAnswered ? ' ob-quiz-card--answered' : ''}${submitted && verdict === 'correct' ? ' ob-quiz-card--correct' : ''}${submitted && verdict === 'incorrect' ? ' ob-quiz-card--incorrect' : ''}`}>
                <div className="ob-quiz-card-header">
                  <span className="ob-quiz-card-number">Question {qIdx + 1}</span>
                  <span className="ob-quiz-card-type">{typeLabel}</span>
                  {!submitted && isAnswered && <span className="ob-quiz-answered-badge">✓ Answered</span>}
                  {submitted && verdict === 'correct' && <span className="ob-quiz-answered-badge">✓ Correct</span>}
                  {submitted && verdict === 'incorrect' && <span className="ob-quiz-answered-badge ob-quiz-answered-badge--wrong">✗ Incorrect</span>}
                </div>
                <div className="ob-quiz-card-text"><RichMarkdown text={q.question_text} /></div>
                <div className="ob-quiz-choices">
                  {q.choices.map((choiceText, cIdx) => {
                    const letter = String.fromCharCode(65 + cIdx);
                    const isThisCorrect = submitted && isCorrectChoice(q, choiceText, cIdx);
                    const isUserPick = userAnswer === choiceText;
                    let rowClass = 'ob-quiz-choice-row';
                    if (isUserPick && !submitted) rowClass += ' ob-quiz-choice-row--selected';
                    if (submitted && isThisCorrect) rowClass += ' ob-quiz-choice-row--correct';
                    if (submitted && isUserPick && !isThisCorrect) rowClass += ' ob-quiz-choice-row--incorrect';
                    return (
                      <button
                        key={cIdx}
                        className={rowClass}
                        onClick={() => handleSelect(qIdx, choiceText)}
                        disabled={submitted}
                      >
                        <span className="ob-quiz-choice-letter">{letter}</span>
                        <span className="ob-quiz-choice-text"><RichMarkdown text={choiceText} /></span>
                      </button>
                    );
                  })}
                </div>
                {submitted && isAnsweredWrong && correctText && (
                  <p className="ob-quiz-correct-answer">
                    Correct answer: <strong>{correctText}</strong>
                  </p>
                )}
                {submitted && q.explanation && (
                  <p className="ob-quiz-explanation"><RichMarkdown text={q.explanation} /></p>
                )}
              </div>
            );
          })}

          {!submitted && (
            <div className="ob-quiz-submit-area">
              <div className="ob-quiz-progress-bar">
                <div className="ob-quiz-progress-fill" style={{ width: `${displayQuestions.length > 0 ? (Object.keys(answers).length / displayQuestions.length) * 100 : 0}%` }} />
              </div>
              <div className="ob-quiz-submit-row">
                <span className={`ob-quiz-submit-status${Object.keys(answers).length === displayQuestions.length ? ' ob-quiz-submit-status--ready' : ''}`}>
                  {Object.keys(answers).length === displayQuestions.length
                    ? '✓ All questions answered'
                    : `${Object.keys(answers).length} / ${displayQuestions.length} answered`}
                </span>
                <button
                  className="onboarding-btn primary"
                  onClick={handleSubmit}
                  disabled={Object.keys(answers).length === 0}
                >
                  Submit Quiz
                </button>
              </div>
            </div>
          )}

          {submitted && score && (
            <div ref={scoreRef} className="ob-quiz-results">
              <div className="ob-quiz-score">
                You scored {score.correct} / {score.total}!
                {score.correct === score.total && ' Perfect! 🎉'}
                {score.correct >= score.total * 0.6 && score.correct < score.total && ' Nice job! 👍'}
                {score.correct < score.total * 0.6 && ' Keep studying — you\'ve got this! 💪'}
              </div>
              <div className="ob-quiz-results-breakdown">
                <span className="ob-quiz-results-stat ob-quiz-results-stat--correct">✓ {score.correct} correct</span>
                <span className="ob-quiz-results-stat ob-quiz-results-stat--incorrect">✗ {score.total - score.correct} incorrect</span>
              </div>
            </div>
          )}

          <p className="ob-quiz-note">
            In the full app, you can also generate <strong>long-form / short-answer</strong> quizzes
            that are graded by AI with detailed feedback.
          </p>
        </div>
      )}
    </div>
  );
}


// ─── Chat Panel (Scout's Desk) ───────────────────────────────────
function ChatPanel({ data, user, courseId }) {
  const [messages, setMessages] = useState([
    {
      role: 'scout',
      text: `Hey ${user?.fullName?.split(' ')[0] || 'there'}! I'm Scout, your AI study assistant. I can help you study, explain concepts, quiz you, and keep you on track. Try asking me something!`,
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    const prompt = text || input.trim();
    if (!prompt || sending) return;

    setInput('');
    setSending(true);
    setHasInteracted(true);

    setMessages(prev => [...prev, { role: 'user', text: prompt }]);

    try {
      const response = await AiConversationsApi.promptWithConversationContext(prompt, {
        endeavor_id: courseId ? Number(courseId) : undefined,
        quality_level: 'fast',
      });

      let reply = '';
      if (response.ok) {
        const json = await response.json();
        reply = json?.response || json?.message || json?.text || json?.answer || 'I received your message!';
      } else {
        reply = "I'm having trouble connecting right now, but don't worry — I'll be ready when you come back to the dashboard!";
      }

      setMessages(prev => [...prev, { role: 'scout', text: reply }]);
    } catch (err) {
      console.warn('[ChatPanel] Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'scout',
        text: "Hmm, I couldn't connect just now. No worries — you can chat with me anytime from the dashboard!",
      }]);
    } finally {
      setSending(false);
    }

    UsageEventsTracker.recordUserAction('ONBOARDING_SCOUT_CHAT', {
      action: 'send_message',
      category: 'onboarding',
      metadata: { courseId },
    });
  }, [input, sending, courseId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="onboarding-ai-panel">
      <div className="ai-mode-header ai-mode-header--chat">
        <span className="ai-mode-header__bar" />
        <p className="ai-mode-header__line">
          <strong>Ask Scout</strong> — Get answers, explanations, and study guidance
        </p>
      </div>

      <div className="ob-chat-container">
        <div className="ob-chat-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`ob-chat-msg-row ${msg.role === 'scout' ? 'ob-chat-msg-row--ai' : 'ob-chat-msg-row--user'}`}>
              {msg.role === 'scout' && (
                <img src="/scout_images/Scout_Thumbsup.png" alt="Scout" className="ob-chat-msg-avatar" />
              )}
              <div className="ob-chat-bubble"><RichMarkdown text={msg.text} /></div>
            </div>
          ))}
          {sending && (
            <div className="ob-chat-msg-row ob-chat-msg-row--ai">
              <img src="/scout_images/Scout_Thumbsup.png" alt="Scout" className="ob-chat-msg-avatar" />
              <div className="ob-chat-bubble" style={{ opacity: 0.6 }}>Thinking...</div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar — pill-shaped like AiChatCard */}
        <div className="ob-chat-input-bar">
          <textarea
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Scout anything..."
            disabled={sending}
            className="ob-chat-textarea"
          />
          <button className="ob-chat-send-btn" onClick={() => sendMessage()} disabled={sending || !input.trim()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>

      {!hasInteracted && (
        <div className="ob-chat-suggestions">
          {SUGGESTED_PROMPTS.map((prompt, idx) => (
            <button
              key={idx}
              className="ob-chat-suggestion-chip"
              onClick={() => sendMessage(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      <p className="ob-chat-footer">
        Scout can quiz you, explain concepts, and help you study — come back anytime from the dashboard.
      </p>
    </div>
  );
}


// ─── Audio Review Panel ──────────────────────────────────────────
function AudioReviewPanel() {
  return (
    <div className="onboarding-ai-panel ob-audio-panel">
      <div className="ai-mode-header ai-mode-header--audio">
        <span className="ai-mode-header__bar" />
        <p className="ai-mode-header__line">
          <strong>Audio</strong> — Study while you move
        </p>
      </div>

      <div className="ob-audio-features">
        <div className="ob-audio-feature">
          <span className="ob-audio-feature-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          </span>
          <div>
            <strong>AI Podcast</strong>
            <p>Two AI hosts discuss your course topics in an engaging conversational format.</p>
          </div>
        </div>
        <div className="ob-audio-feature">
          <span className="ob-audio-feature-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
          </span>
          <div>
            <strong>Audiobook</strong>
            <p>A narrated summary of your materials — like a study guide you can listen to.</p>
          </div>
        </div>
        <div className="ob-audio-feature">
          <span className="ob-audio-feature-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
          </span>
          <div>
            <strong>Lecture Summary</strong>
            <p>Key points from your lectures condensed into a quick audio review.</p>
          </div>
        </div>
      </div>

      <p className="ob-audio-footer">
        Audio generation takes a few minutes — you can start it from your course dashboard after setup.
      </p>
    </div>
  );
}
